import React, { memo, useState, useEffect, useCallback } from "react"
import Box from "@material-ui/core/Box"
import clsx from "clsx"
import { makeStyles } from "@material-ui/core/styles"
import ModalComplete from "@/components/control-panel/control-panel-modal/ModalComplete"
import { controlPanelSidebarOpenComponent } from "@/redux/actions/controlPanelSidebarAction"
import { deselectedParticipant } from "@/redux/actions/controlPanelSelectedParticipantAction"
import { useSelector, useDispatch } from "react-redux"
import useHandleModalShow from "@/hooks/useHandleModalShow"
import IconSparcaComplete from "@/assets/icons/IconSparcaComplete"
import Typography from "@material-ui/core/Typography"
import Loading from "@/components/common/Loading"
import ErrorAccess from "@/components/common/ErrorAccess"
import RequestSnackbar from "@/components/common/RequestSnackbar"
import useHandleRequestSnackbar from "@/hooks/useHandleRequestSnackbar"
import { useParams } from "react-router-dom"
import { getParticipantHaveRoleByProjectId, getParticipantNotRoleByProjectId } from "@/redux/actions/participantAction"
import { getProjectRolesByProjectId } from "@/apis"
import ControlPanelContent from "./control-panel-content"
import ControlPanelSidebar from "./control-panel-sidebar"

const useStyles = makeStyles((theme) => ({
    controlPanelWrapper: {
        flexGrow: 1,
        flexDirection: "column",
        [theme.breakpoints.up("md")]: {
            flexDirection: "row",
        },
    },
    controlPanelContentWrapper: {
        flexGrow: 1,
        overflow: "auto",
    },
    controlPanelSidebarWrapper: {
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(-45deg,#161617,#364556)",
        [theme.breakpoints.up("md")]: {
            flexDirection: "row",
        },
    },
    controlPanelFixSubmitButton: {
        zIndex: 2,
        width: "45px",
        textTransform: "uppercase",
        color: "#fff",
        cursor: "pointer",
        borderRadius: "5px",
        background: theme.palette.primary.main,
        textAlign: "center",
        position: "fixed",
        bottom: "95px",
        height: "auto",
        left: "15px",
    },
    iconWrapper: {
        cursor: "pointer",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
            "& $labelWrapper": {
                clipPath: "polygon(0 0,100% 0,100% 100%,0 100%)",
            },
        },
    },
    iconInnerWrapper: {
        display: "flex",
    },
    labelWrapper: {
        display: "flex",
        position: "absolute",
        height: "100%",
        left: "40px",
        textTransform: "uppercase",
        overflow: "hidden",
        background: theme.palette.primary.main,
        color: "#ffffff",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        transition: "0.3s ease-out",
        clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)",
        "&>p": {
            padding: theme.spacing(2),
            paddingRight: theme.spacing(3),
        },
    },
}))

const ControlPanel = memo(() => {
    const { projectId } = useParams()

    const classes = useStyles()
    const dispatch = useDispatch()

    const {
        modalItemShow: modalCompleteProjectShow,
        handleClose,
        handleModalItemShowChange: handleModalCompleteProjectShowChange,
    } = useHandleModalShow()

    const handleControlPanelContentWrapperClick = useCallback(() => {
        // click outside of progressProgressRing, close edit and sidebar changes to display all
        dispatch(deselectedParticipant())
        dispatch(controlPanelSidebarOpenComponent(process.env.SPARCA_CONTROL_PANEL_SIDEBAR_ICONS_SELECT))
    }, [dispatch])

    const [projectRolesRetrieve, setProjectRolesRetrieve] = useState({
        projectRoles: [],
        loading: true,
        error: false,
        errorMsg: null,
        errorMsgPopup: false,
    })

    const {
        participants,
        loading: participantLoading,
        messagePopup,
        message,
        successStatus,
        mainPageError,
    } = useSelector((state) => state.participantReducer)

    const requestSnackbarValue = useHandleRequestSnackbar(messagePopup)
    const requestSnackbarProjectRoleValue = useHandleRequestSnackbar(projectRolesRetrieve.errorMsgPopup)

    useEffect(() => {
        const retrieveProjectRoles = async () => {
            try {
                const projectRolesResult = await getProjectRolesByProjectId(projectId)
                setProjectRolesRetrieve({
                    projectRoles: projectRolesResult.data.role,
                    loading: false,
                    error: false,
                    errorMsg: null,
                    errorMsgPopup: false,
                })
            } catch (error) {
                setProjectRolesRetrieve({
                    projectRoles: [],
                    loading: false,
                    error: true,
                    errorMsg: "Retrieve project roles failed ",
                    errorMsgPopup: true,
                })
            }
        }
        retrieveProjectRoles()
    }, [])

    useEffect(() => {
        dispatch(getParticipantHaveRoleByProjectId(projectId))
        dispatch(getParticipantNotRoleByProjectId(projectId))
    }, [projectId])

    let errorOutput = ""
    if (projectRolesRetrieve.error) {
        errorOutput = (
            <RequestSnackbar
                status={false}
                message={projectRolesRetrieve.errorMsg}
                snackbarOpen={requestSnackbarProjectRoleValue.snackbarOpen}
                handleSnackbarOpenStatus={requestSnackbarProjectRoleValue.handleSnackbarOpenStatus}
            />
        )
    } else if (!successStatus) {
        errorOutput = (
            <RequestSnackbar
                status={false}
                message={message}
                snackbarOpen={requestSnackbarValue.snackbarOpen}
                handleSnackbarOpenStatus={requestSnackbarValue.handleSnackbarOpenStatus}
            />
        )
    }

    return (
        <>
            {projectRolesRetrieve.loading || participantLoading ? (
                <Box className={clsx(classes.contentWrapper, classes.loading)}>
                    <Loading content="Loading Projects Roles and Participants..." transformX={90} />
                </Box>
            ) : (
                <>
                    {projectRolesRetrieve.error || (!successStatus && mainPageError) ? (
                        <>
                            {errorOutput}
                            <ErrorAccess style={{ minHeight: "200px" }} errorComponent="ControlPanelContent" />
                        </>
                    ) : (
                        <>
                            <Box display="flex" className={classes.controlPanelWrapper}>
                                <Box className={classes.controlPanelContentWrapper} onClick={handleControlPanelContentWrapperClick}>
                                    <ControlPanelContent projectRolesRetrieve={projectRolesRetrieve} participants={participants} />
                                </Box>
                                <Box className={classes.controlPanelSidebarWrapper}>
                                    <ControlPanelSidebar handleModalCompleteProjectShowChange={handleModalCompleteProjectShowChange} />
                                </Box>
                            </Box>
                            <Box className={classes.controlPanelFixSubmitButton}>
                                <Box
                                    px={2}
                                    py={2}
                                    className={classes.iconWrapper}
                                    onClick={() => handleModalCompleteProjectShowChange(true)}
                                >
                                    <Box className={classes.iconInnerWrapper}>
                                        <IconSparcaComplete width="25" fill="white" className={classes.controlPanelSidebarFormIcon} />
                                    </Box>
                                    <Box className={classes.labelWrapper}>
                                        <Typography> Complete Project</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <ModalComplete modalCompleteProjectShow={modalCompleteProjectShow} handleClose={handleClose} />
                        </>
                    )}
                </>
            )}
        </>
    )
})

export default ControlPanel
